import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Grid,
  Alert,
  LinearProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Email,
  Phone,
  Business,
  AttachMoney,
  CalendarToday,
  Person,
  Clear,
} from '@mui/icons-material';
import { getLeads, updateLeadStatus } from '../../../services/request/LoanLeads';
import { PaginationConst } from '../../../services/constants/AppConstant';
import { PageHeader, DataToolbar } from '../../../shared-components/ui';

interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  panEnc?: string | null;
  dob?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  pinCode?: string | null;
  loanType?: string | null;
  status?: string | null;
  requestedAmount?: number;
  amount?: number;
  outstanding?: number;
  interestRate?: number;
  duration?: number;
  durationType?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  emiAmount?: number;
  emiDate?: number;
  paymentChannel?: string | null;
  paymentReferenceId?: string | null;
  createdBy?: string | null;
  createdDate?: string;
  updatedBy?: string | null;
  updatedDate?: string;
  businessName?: string;
  businessType?: string;
}

interface LeadsResponse {
  result: Lead[];
  totalCount: number;
  message: string;
  code: number;
}

const LeadsManagement: React.FC = () => {
  const theme = useTheme();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [statusState, setStatusState] = useState<Record<string, {
    status: string;
    customText: string;
    saving: boolean;
  }>>({});
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PaginationConst.PAGE_SIZE);
  
  // Filter state
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    loanType: '',
    status: '',
    requestedAmount: '',
    duration: '',
    startDate: '',
    createdBy: '',
  });
  
  // Sort state
  const [sort, setSort] = useState('createdDate,desc');

  const STATUS_OPTIONS = [
    { value: 'CREATED', label: 'Created' },
    { value: 'JUNK', label: 'Junk' },
    { value: 'ADDRESSED', label: 'Addressed' },
    { value: 'PROCESSED', label: 'Processed' },
    { value: 'LOAN_ISSUES', label: 'Loan Issues' },
    { value: 'FOLLOW_UP', label: 'Follow Up' },
    { value: 'DISBURSED', label: 'Disbursed' },
    { value: 'LOST', label: 'Lost' },
    { value: 'CUSTOM', label: 'Custom Status' },
  ];
  
  // Detail dialog state
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build filter object with proper query parameter format
      const filterObj: any = {};
      
      if (filters.firstName) filterObj['firstName.contains'] = filters.firstName;
      if (filters.lastName) filterObj['lastName.contains'] = filters.lastName;
      if (filters.email) filterObj['email.contains'] = filters.email;
      if (filters.phone) filterObj['phone.equals'] = filters.phone;
      if (filters.loanType) filterObj['loanType.equals'] = filters.loanType;
      if (filters.status) filterObj['status.equals'] = filters.status;
      if (filters.requestedAmount) filterObj['requestedAmount.greaterThan'] = Number(filters.requestedAmount);
      if (filters.duration) filterObj['duration.equals'] = Number(filters.duration);
      if (filters.startDate) filterObj['startDate.greaterThan'] = filters.startDate;
      if (filters.createdBy) filterObj['createdBy.equals'] = filters.createdBy;

      const response = await getLeads(page, rowsPerPage, filterObj, sort);
      
      if (response?.data) {
        const leadsData: LeadsResponse = response.data;
        setLeads(leadsData.result || []);
        setTotalCount(leadsData.totalCount || 0);
      } else {
        setError('Failed to load leads');
      }
    } catch (err: any) {
      console.error('Error loading leads:', err);
      setError(err?.response?.data?.message || 'Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, sort]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setFilters({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      loanType: '',
      status: '',
      requestedAmount: '',
      duration: '',
      startDate: '',
      createdBy: '',
    });
    setPage(0);
  };

  const handleSortChange = (newSort: string) => {
    setSort(newSort);
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number | undefined): string => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string | null | undefined): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    const s = status?.toUpperCase();
    switch (s) {
      case 'CREATED':
        return 'primary';
      case 'DISBURSED':
      case 'APPROVED':
      case 'PROCESSED':
      case 'ADDRESSED':
        return 'success';
      case 'FOLLOW_UP':
        return 'warning';
      case 'JUNK':
      case 'LOAN_ISSUES':
      case 'LOST':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCurrentStatus = (lead: Lead): string =>
    statusState[lead.id]?.status || lead.status || 'CREATED';

  const handleStatusSelect = async (lead: Lead, value: string) => {
    setStatusState(prev => ({
      ...prev,
      [lead.id]: {
        ...(prev[lead.id] || { customText: '' }),
        status: value,
        saving: prev[lead.id]?.saving || false,
      },
    }));

    if (value !== 'CUSTOM') {
      await doUpdateStatus(lead, value);
    }
  };

  const doUpdateStatus = async (lead: Lead, statusValue: string) => {
    setStatusState(prev => ({
      ...prev,
      [lead.id]: {
        ...(prev[lead.id] || { customText: '' }),
        status: statusValue,
        saving: true,
      },
    }));

    try {
      const payload = {
        status: statusValue,
        amount: lead.amount ?? lead.requestedAmount ?? 0,
        emiAmount: lead.emiAmount ?? 0,
        emiDate: lead.emiDate ?? 0,
      };
      const res = await updateLeadStatus(lead.id, payload);
      if (res.status === 200) {
        // refresh list to reflect backend status
        loadLeads();
      } else {
        setError('Failed to update lead status');
      }
    } catch (err: any) {
      console.error('Error updating lead status:', err);
      setError(err?.response?.data?.message || 'Failed to update lead status');
    } finally {
      setStatusState(prev => ({
        ...prev,
        [lead.id]: {
          ...(prev[lead.id] || { customText: '' }),
          saving: false,
        },
      }));
    }
  };

  const handleCustomTextChange = (leadId: string, text: string) => {
    setStatusState(prev => ({
      ...prev,
      [leadId]: {
        ...(prev[leadId] || { status: 'CUSTOM', saving: false }),
        customText: text,
      },
    }));
  };

  const handleCustomSave = (lead: Lead) => {
    const entry = statusState[lead.id];
    const customLabel = entry?.customText?.trim();
    if (!customLabel) {
      setError('Please enter a custom status');
      return;
    }
    doUpdateStatus(lead, customLabel);
  };

  const activeFiltersCount = Object.values(filters).filter((v) => v !== '').length;

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        eyebrow="Lending · Ops"
        title="Leads management"
        subtitle="Manage and track all loan leads and applications."
        actions={
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadLeads} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {/* Filters */}
      <Card sx={{ mb: 3, p: 3 }}>
        <DataToolbar
          end={
            activeFiltersCount > 0 ? (
              <Button size="small" startIcon={<Clear />} onClick={handleClearFilters} variant="outlined">
                Clear all
              </Button>
            ) : undefined
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList fontSize="small" />
            Filters
            {activeFiltersCount > 0 && (
              <Chip label={activeFiltersCount} size="small" color="primary" />
            )}
          </Typography>
        </DataToolbar>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="First Name (Contains)"
              value={filters.firstName}
              onChange={(e) => handleFilterChange('firstName', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Searches within first name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Last Name (Contains)"
              value={filters.lastName}
              onChange={(e) => handleFilterChange('lastName', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Searches within last name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Email (Contains)"
              value={filters.email}
              onChange={(e) => handleFilterChange('email', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Searches within email"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Phone"
              value={filters.phone}
              onChange={(e) => handleFilterChange('phone', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Loan Type</InputLabel>
              <Select
                value={filters.loanType}
                label="Loan Type"
                onChange={(e) => handleFilterChange('loanType', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOAN_ON_INTEREST">Loan on Interest</MenuItem>
                <MenuItem value="SALARY_BRIDGE">Salary Bridge</MenuItem>
                <MenuItem value="RAW_MATERIAL">Raw Material</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CREATED">Created</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Min Amount (₹)"
              type="number"
              value={filters.requestedAmount}
              onChange={(e) => handleFilterChange('requestedAmount', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Duration (Months)"
              type="number"
              value={filters.duration}
              onChange={(e) => handleFilterChange('duration', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date (From)"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Created By"
              value={filters.createdBy}
              onChange={(e) => handleFilterChange('createdBy', e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>

        {/* Sort Control */}
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sort}
              label="Sort By"
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <MenuItem value="createdDate,desc">Newest First</MenuItem>
              <MenuItem value="createdDate,asc">Oldest First</MenuItem>
              <MenuItem value="requestedAmount,desc">Amount (High to Low)</MenuItem>
              <MenuItem value="requestedAmount,asc">Amount (Low to High)</MenuItem>
              <MenuItem value="firstName,asc">Name (A-Z)</MenuItem>
              <MenuItem value="firstName,desc">Name (Z-A)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Loan Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No leads found. Try adjusting your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    hover
                    sx={{
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {lead.firstName} {lead.lastName}
                      </Typography>
                      {lead.businessName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Business fontSize="inherit" />
                          {lead.businessName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Email fontSize="small" color="action" />
                        {lead.email}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone fontSize="small" color="action" />
                        {lead.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lead.loanType && (
                        <Chip
                          label={lead.loanType.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      )}
                      {lead.interestRate && (
                        <Typography variant="caption" color="text.secondary">
                          {lead.interestRate}% interest
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(lead.requestedAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lead.duration ? `${lead.duration} months` : '-'}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1}>
                        <Chip
                          label={getCurrentStatus(lead) || 'N/A'}
                          size="small"
                          color={getStatusColor(getCurrentStatus(lead))}
                        />
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            label="Status"
                            value={
                              STATUS_OPTIONS.some(
                                o =>
                                  o.value ===
                                  getCurrentStatus(lead).toUpperCase()
                              )
                                ? getCurrentStatus(lead).toUpperCase()
                                : 'CUSTOM'
                            }
                            onChange={e =>
                              handleStatusSelect(
                                lead,
                                e.target.value as string
                              )
                            }
                          >
                            {STATUS_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {(statusState[lead.id]?.status === 'CUSTOM' ||
                          !STATUS_OPTIONS.some(
                            o =>
                              o.value ===
                              (lead.status || '').toUpperCase()
                          )) && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              size="small"
                              label="Custom Status"
                              value={statusState[lead.id]?.customText || ''}
                              onChange={e =>
                                handleCustomTextChange(
                                  lead.id,
                                  e.target.value
                                )
                              }
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleCustomSave(lead)}
                              disabled={statusState[lead.id]?.saving}
                            >
                              Save
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {formatDate(lead.createdDate)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedLead(lead);
                          setDetailDialogOpen(true);
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Rows per page:"
        />
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Lead Details
            </Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
              <Clear />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box sx={{ mt: 1 }}>
              {/* Personal Information Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                  Personal Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Full Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {selectedLead.firstName} {selectedLead.lastName}
                    </Typography>
                  </Grid>
                  {selectedLead.dob && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatDate(selectedLead.dob)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email fontSize="small" />
                      {selectedLead.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone fontSize="small" />
                      {selectedLead.phone}
                    </Typography>
                  </Grid>
                  {selectedLead.panEnc && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">PAN Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.panEnc}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.id && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Lead ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, fontFamily: 'monospace' }}>
                        {selectedLead.id}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Address Information Section */}
              {(selectedLead.addressLine1 || selectedLead.city || selectedLead.state) && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                    Address Information
                  </Typography>
                  <Grid container spacing={3}>
                    {selectedLead.addressLine1 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Address Line 1</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                          {selectedLead.addressLine1}
                        </Typography>
                      </Grid>
                    )}
                    {selectedLead.addressLine2 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Address Line 2</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                          {selectedLead.addressLine2}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                      {selectedLead.city && (
                        <>
                          <Typography variant="caption" color="text.secondary">City</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                            {selectedLead.city}
                          </Typography>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {selectedLead.state && (
                        <>
                          <Typography variant="caption" color="text.secondary">State</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                            {selectedLead.state}
                          </Typography>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {selectedLead.pinCode && (
                        <>
                          <Typography variant="caption" color="text.secondary">Pin Code</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                            {selectedLead.pinCode}
                          </Typography>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Loan Information Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                  Loan Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Loan Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {selectedLead.loanType ? selectedLead.loanType.replace(/_/g, ' ') : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={selectedLead.status || 'N/A'}
                        size="small"
                        color={getStatusColor(selectedLead.status)}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Requested Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                      {formatCurrency(selectedLead.requestedAmount)}
                    </Typography>
                  </Grid>
                  {selectedLead.amount && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Approved Amount</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatCurrency(selectedLead.amount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.outstanding !== undefined && selectedLead.outstanding !== null && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Outstanding Amount</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.error.main }}>
                        {formatCurrency(selectedLead.outstanding)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.interestRate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Interest Rate</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.interestRate}% per month
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.duration && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.duration} months
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.durationType && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Duration Type</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.durationType}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.startDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Start Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatDate(selectedLead.startDate)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.endDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">End Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatDate(selectedLead.endDate)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.emiAmount !== undefined && selectedLead.emiAmount !== null && selectedLead.emiAmount > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">EMI Amount</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatCurrency(selectedLead.emiAmount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.emiDate !== undefined && selectedLead.emiDate !== null && selectedLead.emiDate > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">EMI Date (Day of Month)</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.emiDate}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Payment Information Section */}
              {(selectedLead.paymentChannel || selectedLead.paymentReferenceId) && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                    Payment Information
                  </Typography>
                  <Grid container spacing={3}>
                    {selectedLead.paymentChannel && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Payment Channel</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                          {selectedLead.paymentChannel}
                        </Typography>
                      </Grid>
                    )}
                    {selectedLead.paymentReferenceId && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Payment Reference ID</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, fontFamily: 'monospace' }}>
                          {selectedLead.paymentReferenceId}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Audit Information Section */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                  Audit Information
                </Typography>
                <Grid container spacing={3}>
                  {selectedLead.createdBy && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Created By</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.createdBy}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.createdDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Created Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatDate(selectedLead.createdDate)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.updatedBy && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Updated By</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {selectedLead.updatedBy}
                      </Typography>
                    </Grid>
                  )}
                  {selectedLead.updatedDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Updated Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        {formatDate(selectedLead.updatedDate)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadsManagement;
